import axios, { AxiosInstance, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'

// Types
export interface User {
  id: string
  name: string
  email: string
  avatar_url?: string
  created_at: string
}

export interface Ping {
  id: string
  user_id: string
  lat: number
  lng: number
  photo_url?: string
  note?: string
  created_at: string
  expires_at: string
  status: string
}

export interface PrivateSpace {
  id: string
  owner_id: string
  title: string
  description: string
  lat: number
  lng: number
  hourly_rate: number
  is_active: boolean
  created_at: string
}

export interface Booking {
  id: string
  space_id: string
  renter_id: string
  start_time: string
  end_time: string
  status: string
  created_at: string
}

export interface AuthResponse {
  user: User
  token: string
}

class ApiService {
  private api: AxiosInstance
  private token: string | null = null

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
      timeout: 10000,
    })

    // Load token from localStorage
    this.token = localStorage.getItem('auth_token')

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        const message = error.response?.data?.detail || error.message || 'An error occurred'

        // Don't show toast for certain errors
        const silentErrors = [401, 403]
        if (!silentErrors.includes(error.response?.status)) {
          toast.error(message)
        }

        return Promise.reject(error)
      }
    )
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('auth_token')
  }

  // Authentication
  async register(data: { name: string; email: string; password: string }): Promise<AuthResponse> {
    const response = await this.api.post('/auth/register', data)
    this.setToken(response.data.token)
    return response.data
  }

  async login(data: { email: string; password: string }): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', data)
    this.setToken(response.data.token)
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/auth/me')
    return response.data
  }

  // Pings
  async getPings(lat: number, lng: number, radius_km: number = 5): Promise<Ping[]> {
    const response = await this.api.get('/pings', {
      params: { lat, lng, radius_km }
    })
    return response.data
  }

  async createPing(data: { lat: number; lng: number; photo_url?: string; note?: string }): Promise<Ping> {
    const response = await this.api.post('/pings', data)
    return response.data
  }

  async expirePing(pingId: string): Promise<void> {
    await this.api.post(`/pings/${pingId}/expire`)
  }

  // Private Spaces
  async getPrivateSpaces(lat: number, lng: number, radius_km: number = 5): Promise<PrivateSpace[]> {
    const response = await this.api.get('/private-spaces', {
      params: { lat, lng, radius_km }
    })
    return response.data
  }

  async createPrivateSpace(data: {
    title: string;
    description: string;
    lat: number;
    lng: number;
    hourly_rate: number;
  }): Promise<PrivateSpace> {
    const response = await this.api.post('/private-spaces', data)
    return response.data
  }

  // Bookings
  async createBooking(data: {
    space_id: string;
    start_time: string;
    end_time: string;
  }): Promise<Booking> {
    const response = await this.api.post('/bookings', data)
    return response.data
  }

  async getMyBookings(): Promise<Booking[]> {
    const response = await this.api.get('/bookings/my')
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService
